import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiErr, isPaywall } from "@/lib/api";
import { ILLUSTRATIONS } from "@/lib/profiles";
import { Mic, X, Loader2, Heart, ShieldQuestion, RotateCcw, Send, Bell, Copy, Share2, Check } from "lucide-react";
import { toast } from "sonner";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

const ADULTS = ["Un parent", "Un grand-frère ou une grande-sœur", "Un maître ou une maîtresse", "L'infirmière de l'école", "Un adulte en qui tu as confiance"];

// Flux vocal direct pour l'enfant : il appuie, raconte, LISA répond simplement.
export default function ChildVoice({ open, onClose, name = "" }) {
  const { t, i18n } = useTranslation();
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("record"); // record | loading | result
  const [result, setResult] = useState(null);
  const [showAdults, setShowAdults] = useState(false);
  const [alertStep, setAlertStep] = useState(null); // null | confirm | ready

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch (_) { /* noop */ }
    setListening(false);
  }, []);

  useEffect(() => {
    if (!open) {
      stop(); setText(""); setResult(null); setPhase("record"); setShowAdults(false); setAlertStep(null);
    }
  }, [open, stop]);

  useEffect(() => () => { try { recRef.current?.stop(); } catch (_) { /* noop */ } }, []);

  const start = useCallback(() => {
    if (!SR) { toast.error(t("pages.childVoice.noSR")); return; }
    let rec;
    try { rec = new SR(); } catch (_) { return; }
    rec.lang = i18n.language === "en" ? "en-US" : "fr-FR"; rec.continuous = true; rec.interimResults = true;
    const base = text;
    rec.onresult = (e) => {
      let finalT = "", interimT = "";
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        (r.isFinal ? (finalT += r[0]?.transcript || "") : (interimT += r[0]?.transcript || ""));
      }
      const spoken = (finalT + interimT).replace(/\s+/g, " ").trim();
      setText((base ? base + " " : "") + spoken);
    };
    rec.onerror = (ev) => {
      if (ev?.error === "not-allowed" || ev?.error === "service-not-allowed")
        toast.error(t("pages.childVoice.micNotAllowed"));
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch (_) { /* noop */ }
  }, [text]);

  const send = async () => {
    const narrative = text.trim();
    if (narrative.length < 3) { toast.error(t("pages.childVoice.sayMore")); return; }
    stop(); setPhase("loading");
    try {
      const { data } = await api.post("/ai/situation", { narrative, profile: "child" });
      setResult(data); setPhase("result");
    } catch (e) {
      if (isPaywall(e)) toast.error(t("pages.childVoice.tooMuch"));
      else toast.error(apiErr(e));
      setPhase("record");
    }
  };

  const reset = () => { setText(""); setResult(null); setPhase("record"); setShowAdults(false); setAlertStep(null); };

  // Message pré-écrit : RIEN n'est envoyé automatiquement. L'enfant choisit et confirme.
  const alertMessage = () =>
    (name ? "Bonjour, je m'appelle " + name + ". " : "Bonjour, ") + "j'ai besoin de te parler. Il m'est arrivé quelque chose et ça me touche. "
    + (text.trim() ? "Voici ce qui s'est passé : « " + text.trim() + " ». " : "")
    + "Est-ce qu'on peut en parler ensemble ? Merci.";

  const shareAlert = async () => {
    const msg = alertMessage();
    try {
      if (navigator.share) {
        await navigator.share({ title: t("child.needAdult"), text: msg });
        toast.success(t("pages.childVoice.shareOk"));
      } else {
        window.location.href = "mailto:?subject=" + encodeURIComponent("J'ai besoin de te parler") + "&body=" + encodeURIComponent(msg);
      }
    } catch (_) { /* l'enfant a annulé : rien n'est envoyé */ }
  };

  const copyAlert = async () => {
    try { await navigator.clipboard.writeText(alertMessage()); toast.success(t("pages.childVoice.copied")); }
    catch (_) { toast.error(t("pages.childVoice.micNotAllowed")); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="child-voice" className="fixed inset-0 z-[60] flex flex-col p-5 overflow-y-auto"
        style={{ background: "linear-gradient(160deg,#F3ECFF,#E9F2FF 60%,#FFF3E6)" }}>
        <button data-testid="child-voice-close" onClick={onClose}
          className="self-end w-11 h-11 rounded-full bg-white/80 flex items-center justify-center shadow-sm"
          style={{ color: "var(--accent-strong)" }} aria-label="Fermer">
          <X size={22} />
        </button>

        {phase === "record" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center max-w-md mx-auto w-full">
            <h2 className="font-display text-3xl mb-2" style={{ color: "var(--accent-strong)" }}>{name ? t("pages.childVoice.tellMeName", { name }) : t("pages.childVoice.tellMe")}</h2>
            <p className="text-lg mb-8" style={{ color: "#4A4460" }}>
              {listening ? t("pages.childVoice.listening") : t("pages.childVoice.pressMic")}
            </p>

            <motion.button data-testid="child-voice-mic" onClick={() => (listening ? stop() : start())}
              animate={listening ? { scale: [1, 1.08, 1] } : { scale: 1 }}
              transition={listening ? { repeat: Infinity, duration: 1.2 } : {}}
              className="w-40 h-40 rounded-full flex items-center justify-center shadow-xl mb-8"
              style={{ background: listening ? "linear-gradient(135deg,#F87171,#EF4444)" : "var(--accent-gradient)" }}>
              <Mic size={64} color="#fff" />
            </motion.button>

            {listening && <span className="text-sm mb-4" style={{ color: "#8B5CF6" }}>{t("pages.childVoice.listeningBadge")}</span>}

            {!!text && (
              <div data-testid="child-voice-transcript" className="w-full rounded-3xl bg-white/80 p-5 text-left text-lg mb-6 shadow-sm" style={{ color: "#2C2540" }}>
                {text}
              </div>
            )}

            {!!text && !listening && (
              <div className="w-full flex flex-col gap-3">
                <button data-testid="child-voice-send" onClick={send}
                  className="w-full rounded-3xl py-4 font-display text-xl text-white flex items-center justify-center gap-2 shadow-lg"
                  style={{ background: "var(--accent-gradient)" }}>
                  <Send size={22} /> {t("pages.childVoice.send")}
                </button>
                <button onClick={reset} className="w-full rounded-3xl py-3 font-display text-lg flex items-center justify-center gap-2 bg-white/70" style={{ color: "var(--accent-strong)" }}>
                  <RotateCcw size={18} /> {t("pages.childVoice.retry")}
                </button>
              </div>
            )}
          </div>
        )}

        {phase === "loading" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <Loader2 size={56} className="animate-spin mb-4" style={{ color: "var(--accent-strong)" }} />
            <p className="font-display text-xl" style={{ color: "var(--accent-strong)" }}>{t("pages.childVoice.thinking")}</p>
          </div>
        )}

        {phase === "result" && result && (
          <div className="flex-1 max-w-md mx-auto w-full pb-6">
            <div className="flex items-center gap-2 mb-4">
              <Heart size={26} style={{ color: "#EF4444" }} />
              <h2 className="font-display text-2xl" style={{ color: "var(--accent-strong)" }}>{name ? t("pages.childVoice.thanksName", { name }) : t("pages.childVoice.thanks")}</h2>
            </div>

            {/* Réponse illustrée : la mascotte de LISA accompagne le message */}
            {result.acknowledgement && (
              <div className="rounded-3xl bg-white/85 p-5 mb-4 shadow-sm flex items-start gap-3" data-testid="child-voice-ack">
                <img src={ILLUSTRATIONS.robotMascot} alt="" data-testid="child-voice-illustration"
                  className="w-16 h-16 object-contain shrink-0 child-bounce" />
                <p className="text-lg" style={{ color: "#2C2540" }}>{result.acknowledgement}</p>
              </div>
            )}
            {result.reassurance && (
              <div className="rounded-3xl p-5 text-lg mb-4 text-white shadow-md" style={{ background: "var(--accent-gradient)" }}>
                {result.reassurance}
              </div>
            )}

            <button data-testid="child-voice-need-adult" onClick={() => setShowAdults((v) => !v)}
              className="w-full rounded-3xl py-4 font-display text-lg flex items-center justify-center gap-2 border-2 mb-3"
              style={{ borderColor: "#CBB8F0", background: "#F6F1FF", color: "var(--accent-strong)" }}>
              <ShieldQuestion size={20} /> {t("pages.childVoice.needAdult")}
            </button>
            {showAdults && (
              <div className="rounded-3xl bg-white/85 p-5 mb-3 shadow-sm" data-testid="child-voice-adults">
                <p className="text-base mb-2" style={{ color: "#2C2540" }}>
                  {result.minor_safety?.message || t("pages.childVoice.trustedIntro")}
                </p>
                <ul className="space-y-1.5 mb-4">
                  {(result.minor_safety?.trusted_adults || ADULTS).map((a, i) => (
                    <li key={i} className="text-base flex items-center gap-2" style={{ color: "#4A4460" }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: "var(--accent)" }} /> {a}
                    </li>
                  ))}
                </ul>

                {/* Alerte Adulte — aucun envoi automatique. L'enfant choisit ET confirme. */}
                {alertStep === null && (
                  <button data-testid="child-alert-start" onClick={() => setAlertStep("confirm")}
                    className="w-full rounded-2xl py-3 font-display text-base text-white flex items-center justify-center gap-2"
                    style={{ background: "var(--accent-gradient)" }}>
                    <Bell size={18} /> {t("pages.childVoice.notifyAdult")}
                  </button>
                )}
                {alertStep === "confirm" && (
                  <div className="rounded-2xl p-4 border-2" style={{ borderColor: "#CBB8F0", background: "#FBF8FF" }} data-testid="child-alert-confirm">
                    <p className="text-base mb-1 font-medium" style={{ color: "var(--accent-strong)" }}>{t("pages.childVoice.confirmQ")}</p>
                    <p className="text-sm mb-3" style={{ color: "#4A4460" }}>{t("pages.childVoice.confirmSub")}</p>
                    <div className="flex gap-2">
                      <button data-testid="child-alert-yes" onClick={() => setAlertStep("ready")}
                        className="flex-1 rounded-xl py-2.5 font-display text-white flex items-center justify-center gap-1.5"
                        style={{ background: "var(--accent-gradient)" }}>
                        <Check size={16} /> {t("pages.childVoice.yesPrepare")}
                      </button>
                      <button data-testid="child-alert-no" onClick={() => setAlertStep(null)}
                        className="flex-1 rounded-xl py-2.5 font-display bg-white" style={{ color: "var(--accent-strong)", border: "1px solid #CBB8F0" }}>
                        {t("pages.childVoice.notNow")}
                      </button>
                    </div>
                  </div>
                )}
                {alertStep === "ready" && (
                  <div className="rounded-2xl p-4 bg-white shadow-sm border" style={{ borderColor: "#EADCF7" }} data-testid="child-alert-ready">
                    <p className="text-sm mb-2" style={{ color: "#4A4460" }}>{t("pages.childVoice.msgIntro")}</p>
                    <p className="text-base rounded-xl p-3 mb-3" style={{ background: "#F6F1FF", color: "#2C2540" }} data-testid="child-alert-message">{alertMessage()}</p>
                    <div className="flex gap-2">
                      <button data-testid="child-alert-share" onClick={shareAlert}
                        className="flex-1 rounded-xl py-2.5 font-display text-white flex items-center justify-center gap-1.5"
                        style={{ background: "var(--accent-gradient)" }}>
                        <Share2 size={16} /> {t("pages.childVoice.sendMsg")}
                      </button>
                      <button data-testid="child-alert-copy" onClick={copyAlert}
                        className="rounded-xl py-2.5 px-4 font-display bg-white flex items-center gap-1.5" style={{ color: "var(--accent-strong)", border: "1px solid #CBB8F0" }}>
                        <Copy size={16} /> {t("pages.childVoice.copy")}
                      </button>
                    </div>
                    <p className="text-xs mt-2" style={{ color: "#8B7BB0" }}>{t("pages.childVoice.msgFooter")}</p>
                  </div>
                )}
              </div>
            )}

            <button data-testid="child-voice-again" onClick={reset}
              className="w-full rounded-3xl py-4 font-display text-lg text-white flex items-center justify-center gap-2 shadow-lg"
              style={{ background: "var(--accent-gradient)" }}>
              <RotateCcw size={20} /> {t("pages.childVoice.tellAnother")}
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
